import { Box, Stack } from '@chakra-ui/react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { Skeleton } from '@main/complykit/components/skeleton';
import { Text } from '@main/complykit/components/text';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { BarStackHorizontal } from '@visx/shape';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { Key, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { ChartLegend, type ChartLegendItem } from '../charts/chart-legend';
import {
  CHART_SEGMENT_TOOLTIP_OFFSET,
  ChartSegmentTooltipContent,
  useChartTooltipStyles,
} from '../charts/chart-segment-tooltip';
import { EmptyPlaceholder } from '../placeholder';
import { useChartScaleColors, useChartStructureColors } from '../status-tag/chart-colors';
import { DashboardCard, DashboardCardProps } from './dashboard-card';

const BAR_ROW_ID = 'bar';
const BAR_HEIGHT = 24;
const MIN_LABEL_WIDTH = 28;

export type StackedHBarLegendItem = {
  key: Key;
  label: string;
  color: string;
};

export type StackedHBarSegment = {
  key: Key;
  label: string;
  value: number;
  color: string;
  labelColor?: string;
};

type StackedHBarSegmentTooltipData = {
  label: string;
  percentage: number;
};

export type StackedHBarProps = {
  key: Key;
  label: string;
  organizationId?: string;
  segments: StackedHBarSegment[];
};

export type StackedHBarSegmentClickEvent = {
  bar: StackedHBarProps;
  segment: StackedHBarSegment;
};

export type StackedHBarChartProps = DashboardCardProps & {
  heading: ReactNode;
  legend: StackedHBarLegendItem[];
  isLoading?: boolean;
  bars?: StackedHBarProps[];
  onSegmentClick?: (event: StackedHBarSegmentClickEvent) => void;
  loadingState: StackedHBarChartLoadingProps;
  emptyState: StackedHBarChartEmptyProps;
};

export const StackedHBarChart = ({
  heading,
  legend,
  isLoading,
  bars,
  onSegmentClick,
  loadingState,
  emptyState,
  ...props
}: StackedHBarChartProps) => {
  const hasChartData = Boolean(bars && bars.length > 0);

  const renderContents = () => {
    if (isLoading) {
      return <StackedHBarChartLoading {...loadingState} />;
    }
    if (!hasChartData) {
      return <StackedHBarChartEmpty {...emptyState} />;
    }

    return (
      <DashboardCard.Body overflowY="auto">
        <Stack spacing={4}>
          {bars?.map(({ key, ...bar }) => (
            <StackedHBar key={key} barKey={key} {...bar} onSegmentClick={onSegmentClick} />
          ))}
        </Stack>
      </DashboardCard.Body>
    );
  };

  return (
    <DashboardCard {...props}>
      <DashboardCard.Header heading={heading} />
      {!isLoading && hasChartData ? (
        <Box px={6} pb={2}>
          <StackedHBarLegend items={legend} />
        </Box>
      ) : null}
      {renderContents()}
    </DashboardCard>
  );
};

const StackedHBarLegend = ({ items }: { items: StackedHBarLegendItem[] }) => {
  const legendItems: ChartLegendItem[] = items.map((item) => ({
    key: String(item.key),
    label: item.label,
    color: item.color,
  }));

  return <ChartLegend items={legendItems} />;
};

const StackedHBar = ({
  barKey,
  label,
  organizationId,
  segments,
  onSegmentClick,
}: Omit<StackedHBarProps, 'key'> & {
  barKey: Key;
  onSegmentClick?: (event: StackedHBarSegmentClickEvent) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const { track: trackColor } = useChartStructureColors();
  /* The on-fill fallback for segments that pass no labelColor: the neutral
   * scale's chart label. White cannot carry text on the soft fills. */
  const { label: defaultSegmentLabelColor } = useChartScaleColors('neutral');
  const resolvedTooltipStyles = useChartTooltipStyles();
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } =
    useTooltip<StackedHBarSegmentTooltipData>();

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    const updateWidth = () => {
      setWidth(element.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const activeSegments = useMemo(() => segments.filter((segment) => segment.value > 0), [segments]);

  const keys = useMemo(
    () => activeSegments.map((segment) => String(segment.key)),
    [activeSegments],
  );

  const data = useMemo(
    () => [
      activeSegments.reduce<Record<string, number>>((row, segment) => {
        row[String(segment.key)] = segment.value * 100;
        return row;
      }, {}),
    ],
    [activeSegments],
  );

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, 100],
        range: [0, width],
        round: true,
      }),
    [width],
  );

  const yScale = useMemo(
    () =>
      scaleBand<string>({
        domain: [BAR_ROW_ID],
        range: [BAR_HEIGHT, 0],
      }),
    [],
  );

  const labelColorByKey = useMemo(
    () => new Map(activeSegments.map((segment) => [String(segment.key), segment.labelColor])),
    [activeSegments],
  );

  const colorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: keys,
        range: activeSegments.map((segment) => segment.color),
      }),
    [activeSegments, keys],
  );

  const segmentLabelByKey = useMemo(
    () => new Map(activeSegments.map((segment) => [String(segment.key), segment.label])),
    [activeSegments],
  );

  const handleSegmentMouseMove = (
    event: MouseEvent<SVGRectElement>,
    segmentKey: string,
    percentage: number,
  ) => {
    const container = containerRef.current;
    const point = container ? localPoint(container, event) : localPoint(event);

    if (!point) {
      return;
    }

    showTooltip({
      tooltipData: {
        label: segmentLabelByKey.get(segmentKey) ?? segmentKey,
        percentage,
      },
      tooltipLeft: point.x,
      tooltipTop: point.y,
    });
  };

  return (
    <Stack spacing={1}>
      <Text size="xs" isTruncated color="muted">
        {label}
      </Text>
      <Box ref={containerRef} position="relative">
        <Box borderRadius="lg" overflow="hidden" bg={trackColor} h={`${BAR_HEIGHT}px`}>
          {width > 0 && activeSegments.length > 0 ? (
            <svg width={width} height={BAR_HEIGHT}>
              <BarStackHorizontal
                data={data}
                keys={keys}
                height={BAR_HEIGHT}
                y={() => BAR_ROW_ID}
                xScale={xScale}
                yScale={yScale}
                color={colorScale}
              >
                {(barStacks) =>
                  barStacks.map((barStack) =>
                    barStack.bars.map((bar) => {
                      if (bar.width <= 0) {
                        return null;
                      }

                      const percentage = Math.round(Number(bar.bar.data[bar.key] ?? 0));
                      const labelColor =
                        labelColorByKey.get(String(bar.key)) ?? defaultSegmentLabelColor;
                      const segmentKey = String(bar.key);
                      const segment = activeSegments.find(
                        (entry) => String(entry.key) === segmentKey,
                      );

                      return (
                        <Group key={`${barStack.index}-${bar.index}`}>
                          <rect
                            x={bar.x}
                            y={bar.y}
                            width={bar.width}
                            height={bar.height}
                            fill={bar.color}
                            cursor={onSegmentClick ? 'pointer' : undefined}
                            onMouseMove={(event) =>
                              handleSegmentMouseMove(event, segmentKey, percentage)
                            }
                            onMouseLeave={hideTooltip}
                            onClick={() => {
                              if (!onSegmentClick || !segment) {
                                return;
                              }

                              onSegmentClick({
                                bar: {
                                  key: barKey,
                                  label,
                                  organizationId,
                                  segments,
                                },
                                segment,
                              });
                            }}
                          />
                          {bar.width >= MIN_LABEL_WIDTH ? (
                            <text
                              x={bar.x + bar.width / 2}
                              y={bar.y + bar.height / 2}
                              fill={labelColor}
                              fontFamily="Inter, arial, sans-serif"
                              fontSize={10}
                              fontWeight={600}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              pointerEvents="none"
                            >
                              {percentage}%
                            </text>
                          ) : null}
                        </Group>
                      );
                    }),
                  )
                }
              </BarStackHorizontal>
            </svg>
          ) : null}
        </Box>
        {tooltipOpen && tooltipData ? (
          <TooltipWithBounds
            top={tooltipTop}
            left={tooltipLeft}
            style={resolvedTooltipStyles}
            {...CHART_SEGMENT_TOOLTIP_OFFSET}
          >
            <ChartSegmentTooltipContent
              label={tooltipData.label}
              value={`${tooltipData.percentage}%`}
            />
          </TooltipWithBounds>
        ) : null}
      </Box>
    </Stack>
  );
};

type StackedHBarChartLoadingProps = {
  barsCount: number;
};

const StackedHBarChartLoading = ({ barsCount }: StackedHBarChartLoadingProps) => {
  return (
    <DashboardCard.Body overflowY="auto">
      <Stack spacing={4}>
        {[...Array(barsCount).keys()].map((key) => (
          <Stack key={key} spacing={1}>
            <Skeleton.Text size="xs" />
            <Skeleton.Block size="sm" />
          </Stack>
        ))}
      </Stack>
    </DashboardCard.Body>
  );
};

type StackedHBarChartEmptyProps = {
  heading: ReactNode;
  subheading?: ReactNode;
};

const StackedHBarChartEmpty = ({ heading, subheading }: StackedHBarChartEmptyProps) => {
  return (
    <DashboardCard.Body display="flex" justifyContent="center" alignItems="center">
      <EmptyPlaceholder>
        <EmptyPlaceholder.Icon as={ChartBarIcon} />
        <EmptyPlaceholder.Content>
          <EmptyPlaceholder.Heading>{heading}</EmptyPlaceholder.Heading>
          <EmptyPlaceholder.Subheading>{subheading}</EmptyPlaceholder.Subheading>
        </EmptyPlaceholder.Content>
      </EmptyPlaceholder>
    </DashboardCard.Body>
  );
};
